import { useMemo, useState } from "react";
import { ArrowUpDown, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

function toCSV(columns, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map(esc).join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c])).join(",")).join("\n");
  return `${head}\n${body}`;
}

export function ResultTable({ columns, rows, testid = "chat-result-table" }) {
  const [sortBy, setSortBy] = useState(null);
  const [dir, setDir] = useState("asc");

  const sorted = useMemo(() => {
    if (!sortBy) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortBy, dir]);

  const onSort = (c) => {
    if (sortBy === c) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(c);
      setDir("asc");
    }
  };

  const downloadCSV = () => {
    const blob = new Blob([toCSV(columns, rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sema_result.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={testid} className="rounded-lg border border-border/60">
      <div className="flex items-center justify-between border-b border-border/40 px-3 py-1.5">
        <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{rows.length} rows</span>
        <Button
          variant="ghost"
          size="sm"
          data-testid="chat-csv-export"
          onClick={downloadCSV}
          className="h-7 text-[11px] uppercase tracking-[0.12em]"
        >
          <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
        </Button>
      </div>
      <div className="max-h-[320px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-card">
            <tr>
              {columns.map((c) => (
                <th
                  key={c}
                  onClick={() => onSort(c)}
                  className="cursor-pointer select-none border-b border-border/40 px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
                >
                  <span className="inline-flex items-center gap-1">
                    {c}
                    <ArrowUpDown className="h-3 w-3 opacity-40" />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={i} className="border-b border-border/20 last:border-0 hover:bg-muted/30">
                {columns.map((c) => (
                  <td key={c} className="px-3 py-1.5 tabular-nums">{r[c] == null ? "—" : String(r[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
